import React from 'react';
import { observer } from 'mobx-react';
import { mutate } from 'swr';
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { ModalStep } from '@components/common/multiStepModal';
import { LightFormBuilder } from '@components/common/lightFormBuilder';
import { ApiGetResource, FormConfig, updateNonOauthAccountApi } from '@api';
import useStores from '@stores/useStores';
import { useMultiStepModalStore } from '@components/common/multiStepModal/modalStore';

import { useDesignVariant } from '../../designVariantContext';
import { useAccountManagementStore } from '../../store';
import { getResourceCacheKey } from '../../hooks/useLoadAccountsIntoAccountManagementStore';
import { useNoConnectionTest } from '../../resourceAdapter';

// Standard AMF secret mask: data-retention secrets are masked identically to every
// other account (no last-4 display, no reveal). An empty secret field keeps the
// stored value; typing replaces it.
const MASKED_PLACEHOLDER = '**********';

const lastKeySegment = (key: string | string[]): string =>
  Array.isArray(key) ? key[key.length - 1] : key;

// The option keys rendered as `switchField` toggles for a definition (e.g. MinIO
// `useSSL`, S3 `enableSSE`). Their stored values need boolean coercion on prefill.
const switchOptionKeys = (fields: FormConfig['fields']): Set<string> =>
  new Set(
    fields
      .filter((field) => field.component === 'switchField')
      .map((field) => lastKeySegment(field.key)),
  );

// The backend normalizes boolean options to the string enums `"true"` / `"false"`.
// A non-empty string is truthy in JS, so a stored `"false"` piped straight into a
// `switchField` (which binds via `valuePropName="checked"`) would render the toggle
// ON. Coerce the stored value — string enums or real booleans — to a real boolean so
// the toggle reflects what was saved. Only `"true"`/`true` is ON; everything else OFF.
const toStoredBoolean = (value: unknown): boolean => value === true || value === 'true';

export const AccountEditFormModalStep: React.FC = observer(() => {
  const variant = useDesignVariant();
  const isNext = variant === 'next';

  const { workspaceStore, messagesStore } = useStores();
  const accountManagementStore = useAccountManagementStore();
  const multiStepModalStore = useMultiStepModalStore();
  const {
    selectedEditAccountId,
    compatibleAccountDefinitions,
    form,
    getEditNonOAuthPayload,
    resetSecretFields,
    resourceDefinitionName,
    findAccountById,
    requiresConnectionTest,
    adapter,
  } = accountManagementStore;

  const account = findAccountById(selectedEditAccountId);
  const accountDefinition = compatibleAccountDefinitions.find(
    (def) => def.name === account?.accountDefinitionName,
  );

  const [loading, setLoading] = React.useState(false);

  // The controller is chosen from the adapter, which is fixed for the store's
  // lifetime, so this call is unconditional and its hook order stable even though
  // which implementation runs differs by resource type. Resources with no
  // connectivity gate get the inert controller and `requiresConnectionTest` is false,
  // so nothing below reads it.
  const useConnectionTest = adapter?.connectionTest?.useController ?? useNoConnectionTest;
  const ConnectionTestControl = adapter?.connectionTest?.Control;

  const connectionTest = useConnectionTest({
    type: accountDefinition?.type,
    accountId: account?.id,
  });

  // Prefill is keyed on *which* account is being edited — the account's id and its
  // definition's name — and deliberately not on the identity of the `account` /
  // `accountDefinition` objects themselves.
  //
  // Those objects are rebuilt on every run of the accounts loader effect, which calls
  // `setAccountDefinitions` and `setAccounts` in the same tick. Both store fields are
  // deep `@observable`s, so MobX re-wraps the incoming plain JSON in fresh proxies:
  // a new identity on every reseed, even when it is handed the very same array
  // reference and nothing in the data has changed. Keyed on the objects, this effect
  // re-ran on any background revalidation and called `resetSecretFields()` +
  // `setFieldsValue()` underneath the user — wiping a half-typed secret and reverting
  // every other in-progress edit in the open form.
  //
  // Keyed on the ids it still runs when the form opens (mount always runs it), when
  // the user switches to a different account, and when a late-arriving definition
  // first resolves — but not when the store is merely reseeded with the same account.
  const accountId = account?.id;
  const accountDefinitionName = accountDefinition?.name;

  // A background refresh no longer re-prefills the form, so the user's unsaved input
  // wins over newer server-side values for as long as the form is open. That is the
  // intended trade: saving PATCHes the account with no optimistic-concurrency check,
  // so re-prefilling never protected a concurrent edit from being overwritten — it
  // only destroyed local input, and only if a poll happened to land mid-typing.
  React.useEffect(() => {
    if (!account || !accountDefinition) {
      return;
    }

    // Always clear the secret namespace when the edit form opens. antd preserves
    // field values across Form.Item unmounts, so a secret typed in an earlier edit
    // session in the same modal would otherwise linger in client state and be
    // revealable via the eye toggle. Secret inputs must always open empty.
    resetSecretFields();

    // Pre-populate non-secret option fields from the account's existing values.
    // Secret fields are intentionally left empty so the user must re-enter them if desired.
    // Toggle-typed options (e.g. `useSSL`, `enableSSE`) arrive as the backend's string
    // enums `"true"`/`"false"`; coerce those to real booleans so the switch reflects the
    // stored value (a raw `"false"` string is truthy and would render the toggle ON).
    // Text options pass through untouched.
    const optionKeys = new Set(accountDefinition.config.optionFields);
    const toggleKeys = switchOptionKeys(accountDefinition.uiConfig.form.fields);
    const optionsValues = Object.fromEntries(
      Object.entries(account.options)
        .filter(([k, v]) => optionKeys.has(k) && v != null)
        .map(([k, v]) => [k, toggleKeys.has(k) ? toStoredBoolean(v) : v]),
    );

    form.setFieldsValue({ name: account.name, options: optionsValues });
    // `account` / `accountDefinition` are read above but intentionally absent here:
    // they are the churning identities this effect must not react to (see above).
    // `accountId` / `accountDefinitionName` stand in for them as stable keys.
  }, [accountId, accountDefinitionName, form, resetSecretFields]);

  if (!accountDefinition || !account) {
    return null;
  }

  // Derive the edit-flow form config from the ui-config, which is
  // primarily written for the create flow and requires adjustments to handle
  // secrets correctly in the edit flow.
  const editFormConfig = {
    ...accountDefinition.uiConfig.form,
    fields: accountDefinition.uiConfig.form.fields.map((fieldConfig) => {
      const { key } = fieldConfig;
      // key can be a nested path array (e.g. ["secret", "apiKey"]) or a plain string (e.g. "name")
      const fieldName = Array.isArray(key) ? key[key.length - 1] : key;
      if (account.secret?.[fieldName] != null) {
        // Secret fields are left empty in the form but already have a value stored
        // in the backend. Show the standard mask as a placeholder and mark the
        // field optional so leaving it blank preserves the stored secret (empty
        // secrets are dropped on submit) and typing replaces it.
        return { ...fieldConfig, placeholder: MASKED_PLACEHOLDER, optional: true };
      }
      return fieldConfig;
    }),
  };

  return (
    <ModalStep
      gap="16px"
      disabled={requiresConnectionTest && connectionTest.status !== 'passed'}
      header={{
        title: 'Edit account',
        description: `Update your ${accountDefinition.uiConfig.name} account`,
      }}
      footer={{
        actionButton: {
          text: 'Save account',
          icon: !isNext && <FontAwesomeIcon icon={regular('floppy-disk')} />,
          onClick: async () => {
            try {
              await form.validateFields();
              setLoading(true);

              const payload = getEditNonOAuthPayload();
              await updateNonOauthAccountApi(workspaceStore.id, account.id, payload);

              // The edit is persisted from here on. `mutate` revalidates through the
              // accounts fetcher and `fetchData` rethrows on a failed GET, so awaiting
              // it inside the outer `try` would report "Failed to update account" for
              // an update that succeeded — and would skip the secret reset below. The
              // refresh is a cache side effect, not part of the update's success: the
              // fetcher's own `onError` already surfaces the failed GET, and the list
              // revalidates on focus. (An edit is idempotent, so the misreport merely
              // misleads rather than inviting a duplicate — but the create path has the
              // same shape and there it is not benign.)
              try {
                await mutate(
                  getResourceCacheKey(
                    workspaceStore.id,
                    resourceDefinitionName,
                    ApiGetResource.ACCOUNTS,
                  ),
                );
              } catch {
                // Deliberately swallowed — see above.
              }

              // Drop the entered secret from client state as soon as it is persisted
              // so it can never linger in memory or be revealed if the edit form is
              // re-opened in the same modal session.
              resetSecretFields();

              multiStepModalStore.prev();
            } catch (error) {
              messagesStore.showErrorMessage(
                typeof error === 'string' ? error : 'Failed to update account',
              );
            } finally {
              setLoading(false);
            }
          },
        },
        loading,
      }}
    >
      <LightFormBuilder
        formConfig={editFormConfig}
        form={form}
        variant={variant}
        onValuesChange={requiresConnectionTest ? connectionTest.reset : undefined}
      />
      {requiresConnectionTest && ConnectionTestControl && (
        <ConnectionTestControl
          status={connectionTest.status}
          error={connectionTest.error}
          onTest={connectionTest.test}
          variant={variant}
        />
      )}
    </ModalStep>
  );
});
